/**
 * Lead leakage model (public-facing estimates):
 * monthly leakage ≈ opportunities × value per lead × team multiplier × combined loss rate
 * recoverable ≈ min(monthly leakage × recovery factor, industry cap)
 *
 * Combined loss rate = inquiry + after-hours + follow-up (each a %), capped.
 */

export const COMBINED_LOSS_CAP = 0.4;
export const RECOVERY_FACTOR = 0.68;

/** Scales dollar outputs vs raw formula (2.54 ≈ 2× prior 1.27 tuning). Adjust to taste. */
const MODEL_OUTPUT_SCALE = 2.54;

export const OPPORTUNITY_MIDPOINT = {
  low: 7,
  mid: 20,
  high: 45,
  vhigh: 75,
};

/** Expected gross value of one converted lead / booked customer. */
export const NICHE_LABELS = {
  medspa: { unit: 'patients', verb: 'book', avgValue: 250 },
  insurance: { unit: 'prospects', verb: 'convert', avgValue: 400 },
  mortgage: { unit: 'leads', verb: 'close', avgValue: 1000 },
  vet: { unit: 'clients', verb: 'book', avgValue: 180 },
  law: { unit: 'clients', verb: 'retain', avgValue: 1500 },
  accounting: { unit: 'clients', verb: 'onboard', avgValue: 800 },
  gym: { unit: 'members', verb: 'sign up', avgValue: 120 },
  realestate: { unit: 'leads', verb: 'close', avgValue: 2000 },
  dealership: { unit: 'leads', verb: 'close', avgValue: 1200 },
  trades: { unit: 'calls', verb: 'book', avgValue: 900 },
};

export const RECOVERABLE_CAP_BY_NICHE = {
  medspa: 7600,
  insurance: 10200,
  mortgage: 15200,
  vet: 6400,
  law: 19000,
  accounting: 11400,
  gym: 3800,
  realestate: 20400,
  dealership: 25400,
  trades: 15200,
};

export const TEAM_INFO = {
  solo: { mult: 0.9, label: '1–3 people' },
  small: { mult: 1.0, label: '4–10 people' },
  medium: { mult: 1.1, label: '11–30 people' },
  large: { mult: 1.2, label: '31+ people' },
};

const INQUIRY_LOSS = {
  fast: 0,
  sameday: 0.08,
  slow: 0.18,
  slips: 0.28,
};

const AFTER_HOURS_LOSS = {
  handled: 0,
  voicemail: 0.1,
  miss: 0.18,
  nothing: 0.25,
};

const FOLLOW_UP_LOSS = {
  auto: 0,
  manual: 0.06,
  sometimes: 0.14,
  never: 0.22,
};

const MINOR_LEAKAGE_RATE = 0.04;

/**
 * Pilot = one focused build (2–3 workflows). Pro = full build (5–7 workflows + assistant) when
 * problems span the funnel and/or volume & team justify a wider build.
 */
function recommendPilotOrPro(answers, recoverable) {
  const inquiryBad = answers.inquiry_handling !== 'fast';
  const afterBad = answers.after_hours !== 'handled';
  const followBad = answers.follow_up !== 'auto';
  const gapCount = [inquiryBad, afterBad, followBad].filter(Boolean).length;

  const vol30Plus = answers.monthly_inquiries === 'high' || answers.monthly_inquiries === 'vhigh';
  const vol10Plus =
    answers.monthly_inquiries === 'mid' ||
    answers.monthly_inquiries === 'high' ||
    answers.monthly_inquiries === 'vhigh';
  const bigTeam = answers.team_size === 'medium' || answers.team_size === 'large';

  if (gapCount >= 3) return 'pro';

  if (gapCount === 2 && (bigTeam || vol30Plus)) return 'pro';

  if (gapCount >= 2 && recoverable >= 7000) return 'pro';

  if (bigTeam && gapCount >= 1 && vol30Plus) return 'pro';

  if (gapCount >= 1 && bigTeam && vol10Plus && recoverable >= 5500) return 'pro';

  return 'pilot';
}

function gapCopy(unit) {
  const u = unit.charAt(0).toUpperCase() + unit.slice(1);
  return {
    inquiry: {
      title: `${u} lost to slow or missed responses`,
      fix: 'Immediate acknowledgment with a next-step link on every new inquiry',
    },
    afterHours: {
      title: `${u} lost outside business hours`,
      fix: 'Instant response with a booking link when you are closed',
    },
    followUp: {
      title: `Unbooked ${unit} going cold — follow-up gaps`,
      fix: 'Automated re-engagement for every unbooked inquiry',
    },
    minor: {
      title: 'Minor gaps in response consistency',
      fix: 'Tightening handoffs and response timing across the board',
    },
  };
}

export function calculateReport(answers) {
  const niche = answers.business_type;
  const unit = NICHE_LABELS[niche]?.unit ?? 'inquiries';
  const avgValue = NICHE_LABELS[niche]?.avgValue ?? 800;
  const opportunities = OPPORTUNITY_MIDPOINT[answers.monthly_inquiries] ?? 20;
  const team = TEAM_INFO[answers.team_size] || TEAM_INFO.small;
  const teamMult = team.mult;
  const cap = RECOVERABLE_CAP_BY_NICHE[niche] ?? 15200;

  const r = INQUIRY_LOSS[answers.inquiry_handling] ?? 0;
  const a = AFTER_HOURS_LOSS[answers.after_hours] ?? 0;
  const f = FOLLOW_UP_LOSS[answers.follow_up] ?? 0;
  const sumRates = r + a + f;
  const combinedLoss = Math.min(sumRates, COMBINED_LOSS_CAP);

  const fixes = gapCopy(unit);

  let rawLeakage =
    sumRates <= 0
      ? opportunities * avgValue * teamMult * MINOR_LEAKAGE_RATE
      : opportunities * avgValue * teamMult * combinedLoss;

  rawLeakage *= MODEL_OUTPUT_SCALE;

  let monthlyLeakage = Math.round(rawLeakage);
  let recoverable = Math.round(monthlyLeakage * RECOVERY_FACTOR);

  if (recoverable > cap) {
    recoverable = Math.round(cap);
    monthlyLeakage = Math.round(cap / RECOVERY_FACTOR);
  }

  const annualLeakage = monthlyLeakage * 12;

  let gaps;
  if (sumRates <= 0) {
    gaps = [
      {
        title: fixes.minor.title,
        cost: monthlyLeakage,
        fix: fixes.minor.fix,
      },
    ];
  } else {
    const c0 = Math.round((monthlyLeakage * r) / sumRates);
    const c1 = Math.round((monthlyLeakage * a) / sumRates);
    const c2 = Math.round((monthlyLeakage * f) / sumRates);
    const drift = monthlyLeakage - (c0 + c1 + c2);
    gaps = [
      { title: fixes.inquiry.title, cost: c0 + drift, fix: fixes.inquiry.fix },
      { title: fixes.afterHours.title, cost: c1, fix: fixes.afterHours.fix },
      { title: fixes.followUp.title, cost: c2, fix: fixes.followUp.fix },
    ];
  }

  const recommended = recommendPilotOrPro(answers, recoverable);

  return {
    gaps,
    monthlyLeakage,
    annualLeakage,
    recoverable,
    recommended,
    niche,
    teamLabel: team.label,
  };
}
